/**
 * FormEditorPage
 *
 * Full-page editor for creating and editing forms.
 * Replaces the modal-based FormBuilderModal with a routable page.
 *
 * Routes:
 * - /forms/new - Create new form
 * - /forms/:id/edit - Edit existing form
 *
 * Supports three form types:
 * - survey: Collect responses with custom fields
 * - signup: Slot-based signups for events
 * - poll: Quick polls with voting
 */

import { useEffect, useCallback, lazy, Suspense, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm, FormProvider, useFormContext } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import {
  FileText,
  Type,
  Palette,
  Settings,
  CheckCircle2,
  ClipboardList,
  Users,
  Vote,
  Loader2,
  GitBranch,
  ChevronDown,
} from 'lucide-react';
import { toast } from 'sonner';
import { z } from 'zod';
import { apiFetch } from '@/client/lib/api';
import { cn } from '@/client/lib/utils';
import { FullPageEditorLayout } from '@/client/components/editors';
import { WizardStep, type WizardStepConfig } from '@/client/components/wizard';
import { Input } from '@/client/components/ui/input';
import { Label } from '@/client/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/client/components/ui/select';
import {
  formStyleOptionsSchema,
  DEFAULT_STYLE_OPTIONS,
  DEFAULT_FORM_SETTINGS,
  DEFAULT_SIGNUP_SETTINGS,
  DEFAULT_POLL_SETTINGS,
  FORM_TYPES,
  type FormField,
  type FormType,
  type FormResponse,
  type CreateFormInput,
  type UpdateFormInput,
  type FormSlotResponse,
} from '@/shared/schemas/forms';

// Lazy load field editors for bundle optimization
const FieldsEditor = lazy(() =>
  import('@/client/components/forms/fields-editor').then((m) => ({ default: m.FieldsEditor }))
);
const SlotsEditor = lazy(() =>
  import('@/client/components/forms/slots-editor').then((m) => ({ default: m.SlotsEditor }))
);
const PollQuestionsEditor = lazy(() =>
  import('@/client/components/forms/poll-questions-editor').then((m) => ({
    default: m.PollQuestionsEditor,
  }))
);

// =============================================================================
// Types
// =============================================================================

interface ProjectOption {
  id: string;
  name: string;
  color: string;
}

interface EditableSlot {
  id: string;
  name: string;
  description: string;
  category: string;
  quantity: number;
  startTime: string;
  endTime: string;
  sortOrder: number;
  claimedCount?: number;
  remaining?: number;
}

type FormEditorInput = {
  name: string;
  description?: string;
  projectId: string;
  formType: FormType;
  fields: FormField[];
  slots: EditableSlot[];
  styleOptions: {
    backgroundColor?: string;
    accentColor?: string;
    textColor?: string;
    fontFamily?: 'inter' | 'poppins' | 'merriweather' | 'system';
    borderRadius?: 'none' | 'small' | 'medium' | 'large' | 'full';
    labelPosition?: 'top' | 'left' | 'floating';
  };
  settings: {
    submitButtonText: string;
    successMessage: string;
    redirectUrl?: string;
    maxSubmissions?: number;
    honeypotEnabled: boolean;
    allowMultipleSubmissions: boolean;
    showClaimerNames?: boolean;
    allowComments?: boolean;
    requireEmail?: boolean;
    showResultsBeforeVote?: boolean;
    showResultsAfterVote?: boolean;
    requireEmailToVote?: boolean;
    allowVoteChange?: boolean;
    pollEndDate?: string;
  };
  seoTitle?: string;
  seoDescription?: string;
  createShortLink?: boolean;
  createQRCode?: boolean;
};

// Field schema for editor
const editorFieldSchema = z.object({
  id: z.string(),
  type: z.enum([
    'text',
    'email',
    'textarea',
    'select',
    'checkbox',
    'radio',
    'number',
    'date',
    'poll_question',
  ]),
  label: z.string().min(1, 'Label is required').max(200),
  name: z.string().min(1, 'Field name is required').max(100),
  placeholder: z.string().max(200).optional(),
  helpText: z.string().max(500).optional(),
  required: z.boolean(),
  options: z.array(z.object({ value: z.string(), label: z.string() })).optional(),
  validation: z
    .object({
      minLength: z.number().int().positive().optional(),
      maxLength: z.number().int().positive().optional(),
      min: z.number().optional(),
      max: z.number().optional(),
      pattern: z.string().optional(),
    })
    .optional(),
  conditionalLogic: z
    .object({
      action: z.enum(['show', 'hide']),
      rules: z.array(
        z.object({
          fieldId: z.string(),
          operator: z.enum(['equals', 'not_equals', 'contains', 'is_empty', 'is_not_empty']),
          value: z.unknown().optional(),
        })
      ),
      match: z.enum(['all', 'any']),
    })
    .optional(),
  pollConfig: z
    .object({
      allowMultiple: z.boolean(),
      maxSelections: z.number().int().min(1).optional(),
    })
    .optional(),
  order: z.number().int().min(0),
});

// Slot schema for editor
const editorSlotSchema = z.object({
  id: z.string(),
  name: z.string().min(1, 'Name is required').max(200),
  description: z.string().max(500),
  category: z.string().max(100),
  quantity: z.number().int().positive('Quantity must be at least 1'),
  startTime: z.string(),
  endTime: z.string(),
  sortOrder: z.number().int().min(0),
  claimedCount: z.number().int().min(0).optional(),
  remaining: z.number().int().min(0).optional(),
});

// Main editor schema
const editorSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100),
  description: z.string().max(500).optional(),
  projectId: z.string().min(1, 'Project is required'),
  formType: z.enum(FORM_TYPES),
  fields: z.array(editorFieldSchema),
  slots: z.array(editorSlotSchema),
  styleOptions: formStyleOptionsSchema,
  settings: z.object({
    submitButtonText: z.string().max(50),
    successMessage: z.string().max(500),
    redirectUrl: z.string().url().optional().or(z.literal('')),
    maxSubmissions: z.number().int().positive().optional(),
    honeypotEnabled: z.boolean(),
    allowMultipleSubmissions: z.boolean(),
    showClaimerNames: z.boolean().optional(),
    allowComments: z.boolean().optional(),
    requireEmail: z.boolean().optional(),
    showResultsBeforeVote: z.boolean().optional(),
    showResultsAfterVote: z.boolean().optional(),
    requireEmailToVote: z.boolean().optional(),
    allowVoteChange: z.boolean().optional(),
    pollEndDate: z.string().optional(),
  }),
  seoTitle: z.string().max(70).optional(),
  seoDescription: z.string().max(160).optional(),
  createShortLink: z.boolean().optional(),
  createQRCode: z.boolean().optional(),
});

// =============================================================================
// Wizard Steps Configuration
// =============================================================================

const getWizardSteps = (formType: FormType): WizardStepConfig[] => {
  const steps: WizardStepConfig[] = [
    { id: 'basics', label: 'Basics', icon: FileText },
    {
      id: 'fields',
      label: formType === 'signup' ? 'Slots' : formType === 'poll' ? 'Questions' : 'Fields',
      icon: formType === 'signup' ? Users : formType === 'poll' ? ClipboardList : Type,
    },
  ];

  // Logic step only for survey forms (conditional field visibility)
  if (formType === 'survey') {
    steps.push({ id: 'logic', label: 'Logic', icon: GitBranch, optional: true });
  }

  steps.push(
    { id: 'style', label: 'Style', icon: Palette, optional: true },
    { id: 'settings', label: 'Settings', icon: Settings, optional: true },
    { id: 'publish', label: 'Publish', icon: CheckCircle2 }
  );

  return steps;
};

// =============================================================================
// Loading Fallback
// =============================================================================

function EditorLoadingFallback() {
  return (
    <div className="space-y-4 animate-pulse">
      <div className="h-64 bg-muted rounded-lg flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
      <div className="h-12 bg-muted rounded-lg" />
      <div className="h-12 bg-muted rounded-lg" />
    </div>
  );
}

// =============================================================================
// Step Components
// =============================================================================

interface BasicsStepProps {
  projects: ProjectOption[];
  isEditMode: boolean;
}

function BasicsStep({ projects, isEditMode }: BasicsStepProps) {
  const form = useFormContext<FormEditorInput>();
  const currentFormType = form.watch('formType');

  const handleFormTypeChange = (type: FormType) => {
    form.setValue('formType', type);

    // Apply default settings and fields based on form type (TSK-021: Smart Defaults)
    const currentFields = form.getValues('fields');
    const hasExistingFields = currentFields.length > 0;

    if (type === 'signup') {
      form.setValue(
        'settings.submitButtonText',
        DEFAULT_SIGNUP_SETTINGS.submitButtonText || 'Sign Up'
      );
      form.setValue('settings.successMessage', DEFAULT_SIGNUP_SETTINGS.successMessage || '');
      form.setValue('settings.showClaimerNames', DEFAULT_SIGNUP_SETTINGS.showClaimerNames);
      form.setValue('settings.allowComments', DEFAULT_SIGNUP_SETTINGS.allowComments);
      form.setValue('settings.requireEmail', DEFAULT_SIGNUP_SETTINGS.requireEmail);
      // Signup forms use slots, not fields - clear fields if any
      if (hasExistingFields) {
        form.setValue('fields', []);
      }
    } else if (type === 'poll') {
      form.setValue('settings.submitButtonText', DEFAULT_POLL_SETTINGS.submitButtonText || 'Vote');
      form.setValue('settings.successMessage', DEFAULT_POLL_SETTINGS.successMessage || '');
      form.setValue('settings.showResultsBeforeVote', DEFAULT_POLL_SETTINGS.showResultsBeforeVote);
      form.setValue('settings.showResultsAfterVote', DEFAULT_POLL_SETTINGS.showResultsAfterVote);
      form.setValue('settings.requireEmailToVote', DEFAULT_POLL_SETTINGS.requireEmailToVote);
      form.setValue('settings.allowVoteChange', DEFAULT_POLL_SETTINGS.allowVoteChange);
      // Add default poll question if no fields exist
      if (!hasExistingFields) {
        form.setValue('fields', [
          {
            id: `field_${Date.now()}_poll`,
            type: 'poll_question',
            label: 'What do you prefer?',
            name: 'poll_question',
            required: true,
            order: 0,
            options: [
              { value: 'option_1', label: 'Option 1' },
              { value: 'option_2', label: 'Option 2' },
              { value: 'option_3', label: 'Option 3' },
            ],
          },
        ]);
      }
    } else {
      // Survey form
      form.setValue('settings.submitButtonText', DEFAULT_FORM_SETTINGS.submitButtonText);
      form.setValue('settings.successMessage', 'Thanks for your feedback!');
      // Add default survey fields if no fields exist
      if (!hasExistingFields) {
        form.setValue('fields', [
          {
            id: `field_${Date.now()}_name`,
            type: 'text',
            label: 'Your Name',
            name: 'name',
            placeholder: 'Enter your name',
            required: false,
            order: 0,
          },
          {
            id: `field_${Date.now() + 1}_email`,
            type: 'email',
            label: 'Email Address',
            name: 'email',
            placeholder: 'you@example.com',
            required: true,
            order: 1,
          },
          {
            id: `field_${Date.now() + 2}_feedback`,
            type: 'textarea',
            label: 'Your Feedback',
            name: 'feedback',
            placeholder: 'Tell us what you think...',
            required: false,
            order: 2,
          },
        ]);
      }
    }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      {/* Form Type Selector (only in create mode) */}
      {!isEditMode && (
        <div className="space-y-3">
          <Label>Form Type</Label>
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => handleFormTypeChange('survey')}
              className={cn(
                'flex flex-col items-start p-4 rounded-xl border-2 transition-all text-left',
                currentFormType === 'survey'
                  ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-950/30'
                  : 'border-muted hover:border-muted-foreground/30'
              )}
            >
              <div
                className={cn(
                  'h-10 w-10 rounded-lg flex items-center justify-center mb-2',
                  currentFormType === 'survey'
                    ? 'bg-gradient-to-br from-emerald-500 to-green-600'
                    : 'bg-muted'
                )}
              >
                <ClipboardList
                  className={cn(
                    'h-5 w-5',
                    currentFormType === 'survey' ? 'text-white' : 'text-muted-foreground'
                  )}
                />
              </div>
              <span className="font-medium">Survey Form</span>
              <span className="text-xs text-muted-foreground mt-0.5">
                Collect responses with custom fields
              </span>
            </button>

            <button
              type="button"
              onClick={() => handleFormTypeChange('signup')}
              className={cn(
                'flex flex-col items-start p-4 rounded-xl border-2 transition-all text-left',
                currentFormType === 'signup'
                  ? 'border-violet-500 bg-violet-50 dark:bg-violet-950/30'
                  : 'border-muted hover:border-muted-foreground/30'
              )}
            >
              <div
                className={cn(
                  'h-10 w-10 rounded-lg flex items-center justify-center mb-2',
                  currentFormType === 'signup'
                    ? 'bg-gradient-to-br from-violet-500 to-purple-600'
                    : 'bg-muted'
                )}
              >
                <Users
                  className={cn(
                    'h-5 w-5',
                    currentFormType === 'signup' ? 'text-white' : 'text-muted-foreground'
                  )}
                />
              </div>
              <span className="font-medium">Signup Form</span>
              <span className="text-xs text-muted-foreground mt-0.5">
                Slot-based signups for events
              </span>
            </button>

            <button
              type="button"
              onClick={() => handleFormTypeChange('poll')}
              className={cn(
                'flex flex-col items-start p-4 rounded-xl border-2 transition-all text-left',
                currentFormType === 'poll'
                  ? 'border-amber-500 bg-amber-50 dark:bg-amber-950/30'
                  : 'border-muted hover:border-muted-foreground/30'
              )}
            >
              <div
                className={cn(
                  'h-10 w-10 rounded-lg flex items-center justify-center mb-2',
                  currentFormType === 'poll'
                    ? 'bg-gradient-to-br from-amber-500 to-orange-600'
                    : 'bg-muted'
                )}
              >
                <Vote
                  className={cn(
                    'h-5 w-5',
                    currentFormType === 'poll' ? 'text-white' : 'text-muted-foreground'
                  )}
                />
              </div>
              <span className="font-medium">Poll</span>
              <span className="text-xs text-muted-foreground mt-0.5">Quick polls with voting</span>
            </button>
          </div>
        </div>
      )}

      {/* Form Name */}
      <div className="space-y-2">
        <Label htmlFor="name">
          Form Name <span className="text-destructive">*</span>
        </Label>
        <Input
          id="name"
          {...form.register('name')}
          placeholder={
            currentFormType === 'signup'
              ? 'Volunteer Signup'
              : currentFormType === 'poll'
                ? 'Quick Poll'
                : 'Contact Form'
          }
          autoFocus
        />
        {form.formState.errors.name && (
          <p className="text-sm text-destructive">{form.formState.errors.name.message}</p>
        )}
      </div>

      {/* Description */}
      <div className="space-y-2">
        <Label htmlFor="description">
          Description <span className="text-muted-foreground text-xs">(optional)</span>
        </Label>
        <textarea
          id="description"
          {...form.register('description')}
          rows={3}
          className="w-full px-4 py-2.5 rounded-xl border bg-background focus:outline-none focus:ring-2 focus:ring-primary resize-none"
          placeholder={
            currentFormType === 'signup'
              ? 'Describe your event or what volunteers will be doing…'
              : 'Describe what this form is for…'
          }
        />
      </div>

      {/* Project Selection */}
      <div className="space-y-2">
        <Label>
          Project <span className="text-destructive">*</span>
        </Label>
        {projects.length === 0 ? (
          <p className="text-sm text-muted-foreground py-2">
            No projects available. Create a project first.
          </p>
        ) : (
          <Select
            value={form.watch('projectId')}
            onValueChange={(value) => form.setValue('projectId', value)}
          >
            <SelectTrigger className="w-full max-w-xs rounded-xl">
              <SelectValue placeholder="Select a project" />
            </SelectTrigger>
            <SelectContent>
              {projects.map((project) => (
                <SelectItem key={project.id} value={project.id}>
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: project.color || '#6366f1' }}
                    />
                    {project.name}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {form.formState.errors.projectId && (
          <p className="text-sm text-destructive">{form.formState.errors.projectId.message}</p>
        )}
      </div>
    </div>
  );
}

function FieldsStep() {
  const form = useFormContext<FormEditorInput>();
  const formType = form.watch('formType');
  const fields = form.watch('fields');
  const slots = form.watch('slots');

  // For signup forms, show slots editor
  if (formType === 'signup') {
    const slotsForEditor: FormSlotResponse[] = slots.map((slot, index) => ({
      id: slot.id,
      formId: '',
      name: slot.name,
      description: slot.description || null,
      category: slot.category || null,
      quantity: slot.quantity,
      claimedCount: slot.claimedCount ?? 0,
      remaining: slot.remaining ?? slot.quantity - (slot.claimedCount ?? 0),
      startTime: slot.startTime || null,
      endTime: slot.endTime || null,
      sortOrder: slot.sortOrder ?? index,
      createdAt: '',
      updatedAt: '',
    }));

    return (
      <div className="space-y-4 max-w-3xl">
        <div>
          <h3 className="text-lg font-medium">Signup Slots</h3>
          <p className="text-sm text-muted-foreground">
            Define slots for people to sign up. Drag to reorder.
          </p>
        </div>

        <Suspense fallback={<EditorLoadingFallback />}>
          <SlotsEditor
            value={slotsForEditor}
            onChange={(newSlots) => form.setValue('slots', newSlots)}
            maxSlots={50}
            isPlusUser={true}
          />
        </Suspense>
      </div>
    );
  }

  // For poll forms, show poll questions editor
  if (formType === 'poll') {
    return (
      <div className="max-w-3xl">
        <Suspense fallback={<EditorLoadingFallback />}>
          <PollQuestionsEditor
            value={fields}
            onChange={(newFields) => form.setValue('fields', newFields)}
            maxQuestions={10}
          />
        </Suspense>
      </div>
    );
  }

  // For survey forms, show fields editor
  return (
    <div className="space-y-4 max-w-3xl">
      <div>
        <h3 className="text-lg font-medium">Form Fields</h3>
        <p className="text-sm text-muted-foreground">Drag to reorder, click to expand settings</p>
      </div>

      <Suspense fallback={<EditorLoadingFallback />}>
        <FieldsEditor
          value={fields}
          onChange={(newFields) => form.setValue('fields', newFields)}
          maxFields={25}
          isPlusUser={true}
        />
      </Suspense>
    </div>
  );
}

/**
 * Logic Step - Conditional Field Visibility
 *
 * Surfaces conditional logic as a dedicated step (TSK-021).
 * Shows which fields depend on which answers.
 */
function LogicStep() {
  const form = useFormContext<FormEditorInput>();
  const fields = form.watch('fields');

  // Fields that can be sources for logic rules (have selectable options)
  const sourceFields = fields.filter((f) => ['select', 'radio', 'checkbox'].includes(f.type));

  // Fields that currently have conditional logic
  const fieldsWithLogic = fields.filter((f) => f.conditionalLogic?.rules?.length);

  // Update a field's conditional logic
  const updateFieldLogic = (fieldId: string, logic: FormField['conditionalLogic'] | undefined) => {
    const updatedFields = fields.map((f) =>
      f.id === fieldId ? { ...f, conditionalLogic: logic } : f
    );
    form.setValue('fields', updatedFields);
  };

  // No source fields available
  if (sourceFields.length === 0) {
    return (
      <div className="space-y-6 max-w-2xl">
        <div>
          <h3 className="text-lg font-medium">Conditional Logic</h3>
          <p className="text-sm text-muted-foreground">
            Show or hide fields based on answers to other fields.
          </p>
        </div>

        <div className="p-8 rounded-xl border-2 border-dashed bg-muted/30 text-center">
          <GitBranch className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
          <h4 className="font-medium mb-2">No Trigger Fields Available</h4>
          <p className="text-sm text-muted-foreground max-w-sm mx-auto">
            Add dropdown, radio, or checkbox fields to create logic rules. These field types have
            selectable options that can trigger conditions.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h3 className="text-lg font-medium">Conditional Logic</h3>
        <p className="text-sm text-muted-foreground">
          Show or hide fields based on answers to other fields.
        </p>
      </div>

      {/* Summary of existing rules */}
      {fieldsWithLogic.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-muted-foreground">Active Rules</h4>
          {fieldsWithLogic.map((field) => {
            const logic = field.conditionalLogic!;
            const sourceFieldNames = logic.rules
              .map((rule) => fields.find((f) => f.id === rule.fieldId)?.label)
              .filter(Boolean)
              .join(', ');

            return (
              <div
                key={field.id}
                className="p-3 rounded-lg border bg-card flex items-center justify-between"
              >
                <div>
                  <span className="font-medium">{field.label}</span>
                  <span className="text-muted-foreground mx-2">→</span>
                  <span className="text-sm text-muted-foreground">
                    {logic.action === 'show' ? 'Shows' : 'Hides'} when{' '}
                    <span className="font-medium">{sourceFieldNames}</span>{' '}
                    {logic.match === 'all' ? 'all match' : 'any match'}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => updateFieldLogic(field.id, undefined)}
                  className="text-xs text-muted-foreground hover:text-destructive"
                >
                  Remove
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Fields that can have logic added */}
      <div className="space-y-3">
        <h4 className="text-sm font-medium text-muted-foreground">
          {fieldsWithLogic.length > 0 ? 'Add More Rules' : 'Create Rules'}
        </h4>
        <div className="grid gap-2">
          {fields
            .filter((f) => !f.conditionalLogic?.rules?.length)
            .map((field) => (
              <div
                key={field.id}
                className="p-3 rounded-lg border bg-muted/30 flex items-center justify-between"
              >
                <div>
                  <span className="text-sm">{field.label}</span>
                  <span className="text-xs text-muted-foreground ml-2">({field.type})</span>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    // Create a default rule using the first source field
                    const firstSource = sourceFields[0];
                    if (firstSource) {
                      updateFieldLogic(field.id, {
                        action: 'show',
                        rules: [
                          {
                            fieldId: firstSource.id,
                            operator: 'equals',
                            value: firstSource.options?.[0]?.value || '',
                          },
                        ],
                        match: 'all',
                      });
                    }
                  }}
                  className="text-xs text-primary hover:underline"
                >
                  + Add condition
                </button>
              </div>
            ))}
        </div>
      </div>

      <p className="text-xs text-muted-foreground">
        Tip: For detailed rule editing, expand individual fields in the Fields step.
      </p>
    </div>
  );
}

function StyleStep() {
  return (
    <div className="space-y-6 max-w-2xl">
      <div className="p-8 rounded-xl border-2 border-dashed bg-muted/30 text-center">
        <Palette className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
        <h3 className="font-medium mb-2">Style Editor</h3>
        <p className="text-sm text-muted-foreground">Colors, typography, and visual styling.</p>
        <p className="text-xs text-muted-foreground mt-2">
          Full style editing will be migrated in the next phase.
        </p>
      </div>
    </div>
  );
}

/**
 * Settings Step - Progressive Disclosure
 *
 * Essential settings visible by default, advanced settings collapsed.
 * Form-type-specific settings only shown for relevant types.
 */
function SettingsStep() {
  const form = useFormContext<FormEditorInput>();
  const formType = form.watch('formType');
  const settings = form.watch('settings');
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [typeSpecificOpen, setTypeSpecificOpen] = useState(false);

  // Helper to update nested settings
  const updateSetting = (key: string, value: unknown) => {
    const currentSettings = form.getValues('settings');
    form.setValue('settings', { ...currentSettings, [key]: value });
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h3 className="text-lg font-medium">Form Settings</h3>
        <p className="text-sm text-muted-foreground">
          Configure how your form behaves after submission.
        </p>
      </div>

      {/* Essential Settings - Always Visible */}
      <div className="space-y-4 p-4 rounded-xl border bg-card">
        <h4 className="font-medium text-sm">Submission Settings</h4>

        <div className="space-y-2">
          <Label htmlFor="submitButtonText">Submit Button Text</Label>
          <Input
            id="submitButtonText"
            value={settings.submitButtonText}
            onChange={(e) => updateSetting('submitButtonText', e.target.value)}
            placeholder="Submit"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="successMessage">Success Message</Label>
          <textarea
            id="successMessage"
            value={settings.successMessage}
            onChange={(e) => updateSetting('successMessage', e.target.value)}
            rows={2}
            className="w-full px-3 py-2 rounded-lg border bg-background text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="Thank you for your submission!"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="redirectUrl">
            Redirect URL <span className="text-muted-foreground text-xs">(optional)</span>
          </Label>
          <Input
            id="redirectUrl"
            value={settings.redirectUrl || ''}
            onChange={(e) => updateSetting('redirectUrl', e.target.value || undefined)}
            placeholder="https://example.com/thank-you"
          />
          <p className="text-xs text-muted-foreground">
            Redirect users to this URL after successful submission.
          </p>
        </div>
      </div>

      {/* Advanced Settings - Collapsed by Default */}
      <div className="border rounded-xl overflow-hidden">
        <button
          type="button"
          onClick={() => setAdvancedOpen(!advancedOpen)}
          className="w-full flex items-center justify-between p-4 hover:bg-muted/50 transition-colors"
        >
          <span className="font-medium text-sm">Advanced Settings</span>
          <ChevronDown
            className={cn(
              'h-5 w-5 text-muted-foreground transition-transform duration-200',
              advancedOpen && 'rotate-180'
            )}
          />
        </button>

        {advancedOpen && (
          <div className="border-t p-4 space-y-4 bg-muted/10">
            <div className="space-y-2">
              <Label htmlFor="maxSubmissions">
                Max Submissions <span className="text-muted-foreground text-xs">(optional)</span>
              </Label>
              <Input
                id="maxSubmissions"
                type="number"
                min={1}
                value={settings.maxSubmissions || ''}
                onChange={(e) =>
                  updateSetting(
                    'maxSubmissions',
                    e.target.value ? parseInt(e.target.value, 10) : undefined
                  )
                }
                placeholder="Unlimited"
              />
              <p className="text-xs text-muted-foreground">
                Limit total submissions. Leave empty for unlimited.
              </p>
            </div>

            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={settings.allowMultipleSubmissions}
                onChange={(e) => updateSetting('allowMultipleSubmissions', e.target.checked)}
                className="h-4 w-4 rounded border-gray-300"
              />
              <div>
                <span className="text-sm font-medium">Allow Multiple Submissions</span>
                <p className="text-xs text-muted-foreground">
                  Let users submit the form more than once.
                </p>
              </div>
            </label>
          </div>
        )}
      </div>

      {/* Form Type Specific Settings */}
      {(formType === 'signup' || formType === 'poll') && (
        <div className="border rounded-xl overflow-hidden">
          <button
            type="button"
            onClick={() => setTypeSpecificOpen(!typeSpecificOpen)}
            className="w-full flex items-center justify-between p-4 hover:bg-muted/50 transition-colors"
          >
            <span className="font-medium text-sm">
              {formType === 'signup' ? 'Signup Options' : 'Poll Options'}
            </span>
            <ChevronDown
              className={cn(
                'h-5 w-5 text-muted-foreground transition-transform duration-200',
                typeSpecificOpen && 'rotate-180'
              )}
            />
          </button>

          {typeSpecificOpen && (
            <div className="border-t p-4 space-y-4 bg-muted/10">
              {formType === 'signup' && (
                <>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.showClaimerNames ?? true}
                      onChange={(e) => updateSetting('showClaimerNames', e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    <div>
                      <span className="text-sm font-medium">Show Claimer Names</span>
                      <p className="text-xs text-muted-foreground">
                        Display names of people who claimed slots.
                      </p>
                    </div>
                  </label>

                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.allowComments ?? true}
                      onChange={(e) => updateSetting('allowComments', e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    <div>
                      <span className="text-sm font-medium">Allow Comments</span>
                      <p className="text-xs text-muted-foreground">
                        Let people add notes when signing up.
                      </p>
                    </div>
                  </label>

                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.requireEmail ?? true}
                      onChange={(e) => updateSetting('requireEmail', e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    <div>
                      <span className="text-sm font-medium">Require Email</span>
                      <p className="text-xs text-muted-foreground">
                        Require email address for signups.
                      </p>
                    </div>
                  </label>
                </>
              )}

              {formType === 'poll' && (
                <>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.showResultsBeforeVote ?? false}
                      onChange={(e) => updateSetting('showResultsBeforeVote', e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    <div>
                      <span className="text-sm font-medium">Show Results Before Vote</span>
                      <p className="text-xs text-muted-foreground">
                        Display current results before user votes.
                      </p>
                    </div>
                  </label>

                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.showResultsAfterVote ?? true}
                      onChange={(e) => updateSetting('showResultsAfterVote', e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    <div>
                      <span className="text-sm font-medium">Show Results After Vote</span>
                      <p className="text-xs text-muted-foreground">
                        Display results after user has voted.
                      </p>
                    </div>
                  </label>

                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.allowVoteChange ?? false}
                      onChange={(e) => updateSetting('allowVoteChange', e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    <div>
                      <span className="text-sm font-medium">Allow Vote Change</span>
                      <p className="text-xs text-muted-foreground">
                        Let users change their vote after submitting.
                      </p>
                    </div>
                  </label>

                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.requireEmailToVote ?? false}
                      onChange={(e) => updateSetting('requireEmailToVote', e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    <div>
                      <span className="text-sm font-medium">Require Email to Vote</span>
                      <p className="text-xs text-muted-foreground">
                        Require email address before voting.
                      </p>
                    </div>
                  </label>

                  <div className="space-y-2">
                    <Label htmlFor="pollEndDate">
                      Poll End Date{' '}
                      <span className="text-muted-foreground text-xs">(optional)</span>
                    </Label>
                    <Input
                      id="pollEndDate"
                      type="datetime-local"
                      value={settings.pollEndDate || ''}
                      onChange={(e) => updateSetting('pollEndDate', e.target.value || undefined)}
                    />
                    <p className="text-xs text-muted-foreground">
                      Automatically close voting at this time.
                    </p>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PublishStep({ isEditMode }: { isEditMode: boolean }) {
  const form = useFormContext<FormEditorInput>();
  const name = form.watch('name');
  const formType = form.watch('formType');
  const fields = form.watch('fields');
  const slots = form.watch('slots');

  const itemCount =
    formType === 'signup'
      ? slots.length
      : formType === 'poll'
        ? fields.filter((f) => f.type === 'poll_question').length
        : fields.length;

  const itemLabel = formType === 'signup' ? 'slots' : formType === 'poll' ? 'questions' : 'fields';

  return (
    <div className="space-y-6 max-w-2xl">
      <div className="p-6 rounded-xl border bg-card">
        <h3 className="font-medium mb-4">Ready to Publish</h3>
        <dl className="space-y-3 text-sm">
          <div className="flex justify-between">
            <dt className="text-muted-foreground">Form Name</dt>
            <dd className="font-medium">{name || '(untitled)'}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-muted-foreground">Form Type</dt>
            <dd className="font-medium capitalize">{formType}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-muted-foreground">
              {itemLabel.charAt(0).toUpperCase() + itemLabel.slice(1)}
            </dt>
            <dd className="font-medium">
              {itemCount} {itemLabel}
            </dd>
          </div>
        </dl>
      </div>
      <p className="text-sm text-muted-foreground">
        {isEditMode
          ? 'Click "Save" to update your form.'
          : 'Click "Publish" to make your form live.'}
      </p>
    </div>
  );
}

// =============================================================================
// Main Page Component
// =============================================================================

export function FormEditorPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const isEditMode = !!id;
  const typeFromUrl = searchParams.get('type') as FormType | null;

  // Fetch projects
  const { data: projectsData, isLoading: isLoadingProjects } = useQuery({
    queryKey: ['projects'],
    queryFn: async () => {
      const response = await apiFetch('/api/projects');
      if (!response.ok) return { data: [] };
      return response.json();
    },
  });

  const projects: ProjectOption[] = projectsData?.data || [];

  // Fetch existing form for edit mode
  const { data: existingForm, isLoading: isLoadingForm } = useQuery<FormResponse>({
    queryKey: ['form', id],
    queryFn: async () => {
      const response = await apiFetch(`/api/forms/${id}`);
      if (!response.ok) throw new Error('Failed to fetch form');
      const data = await response.json();
      return data.data;
    },
    enabled: isEditMode,
  });

  // Form setup
  const form = useForm<FormEditorInput>({
    resolver: zodResolver(editorSchema),
    defaultValues: {
      name: '',
      description: '',
      projectId: '',
      formType: typeFromUrl || 'survey',
      fields: [],
      slots: [],
      styleOptions: DEFAULT_STYLE_OPTIONS,
      settings: DEFAULT_FORM_SETTINGS,
      seoTitle: '',
      seoDescription: '',
      createShortLink: true,
      createQRCode: true,
    },
  });

  const formType = form.watch('formType');
  const wizardSteps = getWizardSteps(formType);

  // Load existing form data
  useEffect(() => {
    if (existingForm && isEditMode) {
      const editableSlots: EditableSlot[] = (existingForm.slots || []).map((slot, index) => ({
        id: slot.id,
        name: slot.name,
        description: slot.description || '',
        category: slot.category || '',
        quantity: slot.quantity,
        startTime: slot.startTime || '',
        endTime: slot.endTime || '',
        sortOrder: slot.sortOrder ?? index,
        claimedCount: slot.claimedCount,
        remaining: slot.remaining,
      }));

      form.reset({
        name: existingForm.name,
        description: existingForm.description || '',
        projectId: existingForm.projectId,
        formType: existingForm.formType || 'survey',
        fields: existingForm.fields || [],
        slots: editableSlots,
        styleOptions: existingForm.styleOptions || DEFAULT_STYLE_OPTIONS,
        settings: existingForm.settings || DEFAULT_FORM_SETTINGS,
        seoTitle: existingForm.seoTitle || '',
        seoDescription: existingForm.seoDescription || '',
        createShortLink: !existingForm.linkId,
        createQRCode: true,
      });
    }
  }, [existingForm, isEditMode, form]);

  const isDirty = form.formState.isDirty;

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: FormEditorInput) => {
      const payload: CreateFormInput = {
        name: data.name,
        description: data.description,
        projectId: data.projectId,
        formType: data.formType,
        fields: data.formType === 'survey' || data.formType === 'poll' ? data.fields : [],
        styleOptions: data.styleOptions,
        settings: data.settings,
        seoTitle: data.seoTitle,
        seoDescription: data.seoDescription,
      };

      const response = await apiFetch('/api/forms', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to create form');
      }

      const result = await response.json();
      const formId = result.id || result.data?.id;

      // For signup forms, create slots after form creation
      if (data.formType === 'signup' && data.slots.length > 0 && formId) {
        for (const slot of data.slots) {
          await apiFetch(`/api/forms/${formId}/slots`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              name: slot.name,
              description: slot.description || undefined,
              category: slot.category || undefined,
              quantity: slot.quantity,
              startTime: slot.startTime || undefined,
              endTime: slot.endTime || undefined,
            }),
          });
        }
      }

      // Publish the form
      await apiFetch(`/api/forms/${formId}/publish`, { method: 'POST' });

      // Generate short link and QR code if requested
      if ((data.createShortLink || data.createQRCode) && formId) {
        try {
          const assetsResponse = await apiFetch(`/api/forms/${formId}/generate-assets`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              createShortLink: data.createShortLink ?? true,
              createQRCode: data.createQRCode ?? true,
            }),
          });

          if (assetsResponse.ok) {
            const assetsResult = await assetsResponse.json();
            if (assetsResult.link?.id) {
              await apiFetch(`/api/forms/${formId}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ linkId: assetsResult.link.id }),
              });
            }
          }
        } catch {
          console.warn('Failed to generate assets for form');
        }
      }

      return result;
    },
    onSuccess: () => {
      toast.success('Form published successfully!');
      queryClient.invalidateQueries({ queryKey: ['forms'] });
      queryClient.invalidateQueries({ queryKey: ['links'] });
      queryClient.invalidateQueries({ queryKey: ['qr-codes'] });
      navigate('/forms');
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (data: FormEditorInput) => {
      const payload: UpdateFormInput = {
        name: data.name,
        description: data.description || null,
        formType: data.formType,
        fields: data.formType === 'survey' || data.formType === 'poll' ? data.fields : [],
        styleOptions: data.styleOptions,
        settings: data.settings,
        seoTitle: data.seoTitle || null,
        seoDescription: data.seoDescription || null,
      };

      const response = await apiFetch(`/api/forms/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to update form');
      }

      // Handle slot changes for signup forms
      if (data.formType === 'signup' && id) {
        const existingSlotIds = new Set((existingForm?.slots || []).map((s) => s.id));
        const currentSlotIds = new Set(data.slots.map((s) => s.id));

        // Delete removed slots
        for (const existingSlot of existingForm?.slots || []) {
          if (!currentSlotIds.has(existingSlot.id)) {
            await apiFetch(`/api/forms/${id}/slots/${existingSlot.id}`, {
              method: 'DELETE',
            });
          }
        }

        // Create new slots or update existing ones
        for (const slot of data.slots) {
          const isNewSlot = slot.id.startsWith('temp_');
          const slotPayload = {
            name: slot.name,
            description: slot.description || undefined,
            category: slot.category || undefined,
            quantity: slot.quantity,
            startTime: slot.startTime || undefined,
            endTime: slot.endTime || undefined,
          };

          if (isNewSlot) {
            await apiFetch(`/api/forms/${id}/slots`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(slotPayload),
            });
          } else if (existingSlotIds.has(slot.id)) {
            await apiFetch(`/api/forms/${id}/slots/${slot.id}`, {
              method: 'PATCH',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(slotPayload),
            });
          }
        }

        // Reorder slots
        const orderedSlotIds = data.slots.filter((s) => !s.id.startsWith('temp_')).map((s) => s.id);
        if (orderedSlotIds.length > 0) {
          await apiFetch(`/api/forms/${id}/slots/reorder`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ slotIds: orderedSlotIds }),
          });
        }
      }

      return response.json();
    },
    onSuccess: () => {
      toast.success('Form updated successfully!');
      queryClient.invalidateQueries({ queryKey: ['forms'] });
      queryClient.invalidateQueries({ queryKey: ['form', id] });
      navigate('/forms');
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  // Handle complete
  const handleComplete = async () => {
    const isValid = await form.trigger();
    if (!isValid) {
      toast.error('Please fix the errors before publishing');
      return;
    }

    const data = form.getValues();

    if (isEditMode) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  // Step validation
  const validateStep = useCallback(
    async (stepIndex: number): Promise<boolean> => {
      const step = wizardSteps[stepIndex];
      if (!step) return true;

      switch (step.id) {
        case 'basics': {
          const result = await form.trigger(['name', 'projectId', 'formType']);
          if (!result) {
            toast.error('Please fill in all required fields');
          }
          return result;
        }
        case 'fields': {
          const currentFormType = form.getValues('formType');
          if (currentFormType === 'survey') {
            const fields = form.getValues('fields');
            if (fields.length === 0) {
              toast.error('Please add at least one field');
              return false;
            }
          } else if (currentFormType === 'poll') {
            const fields = form.getValues('fields');
            const pollQuestions = fields.filter((f) => f.type === 'poll_question');
            if (pollQuestions.length === 0) {
              toast.error('Please add at least one question');
              return false;
            }
          } else {
            const slots = form.getValues('slots');
            if (slots.length === 0) {
              toast.error('Please add at least one slot');
              return false;
            }
          }
          return true;
        }
        default:
          return true;
      }
    },
    [form, wizardSteps]
  );

  const isLoading = isEditMode && (isLoadingForm || isLoadingProjects);

  // Determine accent color based on form type
  const accentColor = formType === 'signup' ? 'violet' : formType === 'poll' ? 'amber' : 'emerald';

  return (
    <FormProvider {...form}>
      <FullPageEditorLayout
        title={isEditMode ? 'Edit Form' : 'Create Form'}
        subtitle={
          formType === 'signup'
            ? 'Create slots for volunteers, potlucks, and more'
            : formType === 'poll'
              ? 'Create quick polls for your audience to vote on'
              : 'Build a form for surveys and lead capture'
        }
        steps={wizardSteps}
        initialStep={0}
        isEditMode={isEditMode}
        isLoading={isLoading}
        isDirty={isDirty}
        onComplete={handleComplete}
        completeButtonText={isEditMode ? 'Save' : 'Publish'}
        accentColor={accentColor}
        backPath="/forms"
        hasPreview={false}
        validateStep={validateStep}
      >
        <WizardStep stepId="basics">
          <BasicsStep projects={projects} isEditMode={isEditMode} />
        </WizardStep>

        <WizardStep stepId="fields">
          <FieldsStep />
        </WizardStep>

        <WizardStep stepId="logic">
          <LogicStep />
        </WizardStep>

        <WizardStep stepId="style">
          <StyleStep />
        </WizardStep>

        <WizardStep stepId="settings">
          <SettingsStep />
        </WizardStep>

        <WizardStep stepId="publish">
          <PublishStep isEditMode={isEditMode} />
        </WizardStep>
      </FullPageEditorLayout>
    </FormProvider>
  );
}
